//
//  auth.ts
//  TrajeXY Signet Server
//
//  Copyright © 2026 TrajeXY. All rights reserved.
//
//  Created: September 23, 2026
//  Author: Bob Keathley
//
//  Session store for the console. The bearer token lives in memory and in
//  per-tab sessionStorage only, never localStorage or a cookie. Storage failures
//  are swallowed so private windows still work. Tenant and platform role lists
//  are kept separate on purpose (RB-13); a role lives on exactly one plane.
//
// Session store for the console. The bearer token is a secret: it lives in memory and, as a
// per-tab convenience, in sessionStorage (cleared when the tab closes; never localStorage, never
// a cookie the API would read). Every read/write of storage is wrapped -- private windows and
// blocked site data must not break the console.
import { useSyncExternalStore } from 'react'
import { api } from './api/client'
import { isOk } from './api/envelope'

// Two role vocabularies, never one (spec §3, RB-13 Part D): a role lives on exactly one plane and
// the two share no names. `require_role(min, plane)` on the server checks plane before rank; the
// console mirrors that split rather than a single `Role` union so nav/gating can't accidentally
// treat a platform role as a point on the tenant ladder or vice versa.
export const TENANT_ROLES = ['auditor', 'member', 'team-admin', 'security-admin', 'owner'] as const
export type TenantRole = (typeof TENANT_ROLES)[number]
export const PLATFORM_ROLES = ['platform-support', 'platform-operator', 'catalogue-publisher', 'platform-owner'] as const
export type PlatformRole = (typeof PLATFORM_ROLES)[number]
export type Role = TenantRole | PlatformRole

export function isTenantRole(role: string): role is TenantRole {
  return (TENANT_ROLES as readonly string[]).includes(role)
}
export function isPlatformRole(role: string): role is PlatformRole {
  return (PLATFORM_ROLES as readonly string[]).includes(role)
}

export type User = {
  id: string
  tenant_id: string
  email: string
  display_name: string
  role: Role
  is_active: boolean
  created_at: number
  last_login_at: number | null
  acting_tenant_id?: string
  must_change_password?: boolean
}
/** `password_change_required`: signed in with a temp password (SIG-66 §9); only /auth/me, logout and the password change are allowed until it is changed. */
export type Session = { token: string; expires_at: number; user: User; password_change_required?: boolean }

const KEY = 'signet.console.session'
let session: Session | null = load()
const listeners = new Set<() => void>()

function load(): Session | null {
  try {
    const raw = globalThis.sessionStorage?.getItem(KEY)
    if (!raw) return null
    const s = JSON.parse(raw) as Session
    return s && s.token && s.expires_at * 1000 > Date.now() ? s : null
  } catch {
    return null
  }
}
function persist(s: Session | null) {
  try {
    if (s) globalThis.sessionStorage?.setItem(KEY, JSON.stringify(s))
    else globalThis.sessionStorage?.removeItem(KEY)
  } catch {
    /* storage unavailable: memory only */
  }
}
function set(s: Session | null) {
  session = s
  persist(s)
  listeners.forEach((l) => l())
}

export const auth = {
  get session() {
    return session
  },
  get token() {
    return session?.token ?? null
  },
  subscribe(l: () => void) {
    listeners.add(l)
    return () => listeners.delete(l)
  },
  async login(email: string, password: string): Promise<{ ok: true } | { ok: false; error: string }> {
    const r = await api<{ token: string; expires_at: number; user: User; password_change_required?: boolean }>('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    })
    if (!isOk(r)) return { ok: false, error: r.error.code === 'unauthorized' ? 'Invalid email or password.' : r.error.message }
    set({ token: r.data.token, expires_at: r.data.expires_at, user: r.data.user, password_change_required: r.data.password_change_required === true || r.data.user.must_change_password === true })
    return { ok: true }
  },
  /** Adopts a token minted elsewhere (e.g. `POST /v1/platform/assume`) as this tab's session. */
  async adopt(token: string, expiresAt: number): Promise<{ ok: true } | { ok: false; error: string }> {
    const r = await api<User>('/auth/me', {}, token)
    if (!isOk(r)) return { ok: false, error: r.error.message }
    set({ token, expires_at: expiresAt, user: r.data })
    return { ok: true }
  },
  async logout() {
    const t = session?.token
    set(null)
    if (t) await api('/auth/logout', { method: 'POST' }, t)
  },
  /** Re-validate against the API; drops the session if it is no longer accepted. */
  async refresh() {
    if (!session) return
    const r = await api<User>('/auth/me', {}, session.token)
    if (isOk(r)) set({ ...session, user: r.data, password_change_required: r.data.must_change_password === true || session.password_change_required })
    else if (r.error.code === 'unauthorized') set(null)
  },
  /** Called by the API client on any `403 password_change_required`: force the change-password screen. */
  requirePasswordChange() {
    if (session && !session.password_change_required) set({ ...session, password_change_required: true })
  },
  /** After a successful password change: the flag is cleared; this session stays signed in (others are revoked server-side). */
  passwordChanged() {
    if (session) set({ ...session, password_change_required: false, user: { ...session.user, must_change_password: false } })
  },
  /** Called by the API client on any 401 so a revoked/expired session logs out everywhere. */
  expire() {
    if (session) set(null)
  },
}

export function useSession(): Session | null {
  return useSyncExternalStore(auth.subscribe, () => session, () => session)
}

// Rank within a plane (§3). `catalogue-publisher` and `platform-operator` sit at the same rank --
// they're siblings with different powers, not one above the other -- so rank alone gates
// "at least this senior", never "can publish the catalogue" (that's `canPublishCatalogue` below).
const TENANT_RANK: Record<TenantRole, number> = { auditor: 1, member: 1, 'team-admin': 2, 'security-admin': 3, owner: 4 }
const PLATFORM_RANK: Record<PlatformRole, number> = { 'platform-support': 1, 'platform-operator': 2, 'catalogue-publisher': 2, 'platform-owner': 3 }

/** Tenant-plane rank check. A platform role (or anything unrecognised) is never "at least" anything here. */
export const atLeast = (role: string | undefined, min: TenantRole): boolean =>
  !!role && isTenantRole(role) && TENANT_RANK[role] >= TENANT_RANK[min]

/** Platform-plane rank check -- mirrors `atLeast` for the other vocabulary. */
export const atLeastPlatform = (role: string | undefined, min: PlatformRole): boolean =>
  !!role && isPlatformRole(role) && PLATFORM_RANK[role] >= PLATFORM_RANK[min]

// Platform-plane gates below are by role NAME, not rank (27 §2): `platform-operator` and
// `catalogue-publisher` sit at the same rank but are deliberately separate roles with disjoint
// jobs ("publishing policy every tenant adopts is a different act from running the service") --
// `atLeastPlatform` would wrongly let one stand in for the other, so none of these use it.

/** §6g: only `platform-owner` and `platform-support` may assume a tenant. */
export const canAssumeTenant = (role: string | undefined): boolean =>
  role === 'platform-owner' || role === 'platform-support'

/** §6g: `catalogue-publisher`+ publishes/retires platform mandate templates; `platform-operator` cannot. */
export const canPublishCatalogue = (role: string | undefined): boolean =>
  role === 'platform-owner' || role === 'catalogue-publisher'

/** §6g/27 §2: tenants (provisioning), platform users and the audit log are `platform-operator`'s
 * job (running the service) or `platform-owner`'s -- `catalogue-publisher` and `platform-support`
 * do neither. */
export const canManagePlatform = (role: string | undefined): boolean =>
  role === 'platform-owner' || role === 'platform-operator'

/** A tenant role may grant any tenant role at or below its own rank (§3); same rule, other vocabulary, for platform. */
export const grantableTenantRoles = (role: string | undefined): TenantRole[] =>
  isTenantRole(role ?? '') ? TENANT_ROLES.filter((r) => TENANT_RANK[r] <= TENANT_RANK[role as TenantRole]) : []
export const grantablePlatformRoles = (role: string | undefined): PlatformRole[] =>
  isPlatformRole(role ?? '') ? PLATFORM_ROLES.filter((r) => PLATFORM_RANK[r] <= PLATFORM_RANK[role as PlatformRole]) : []
